import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useRoute, useLocation } from 'wouter'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Tooltip } from '@/components/ui/tooltip'
import { useToast } from '@/hooks/use-toast'
import { apiRequest, formatDuration, getBaseUrl } from '@/lib/utils'
import { 
  ArrowLeft, 
  Download, 
  Clock, 
  FileText, 
  MapPin, 
  Calendar, 
  CheckCircle, 
  Loader2,
  AlertCircle,
  BarChart3,
  Copy,
  RefreshCw,
  Eye,
  Zap,
  Square,
  AlertTriangle,
  ChevronDown,
  Table
} from 'lucide-react'
import AnalyticsHeader from '@/components/AnalyticsHeader'

export default function ResultsPage() {
  const [match, params] = useRoute('/results/:jobId')
  const [, setLocation] = useLocation()
  const [progress, setProgress] = useState(null)
  const [eventSource, setEventSource] = useState(null)
  const [showLogs, setShowLogs] = useState(false)
  const [logs, setLogs] = useState([])
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false)
  const { toast } = useToast()

  const jobId = params?.jobId

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDownloadDropdown && !event.target.closest('[data-dropdown-container]')) {
        setShowDownloadDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDownloadDropdown])

  // Handle stopping the job
  const handleStopJob = async () => {
    try {
      await apiRequest(`/jobs/${jobId}`, { method: 'DELETE' })
      // The real-time updates will handle UI changes automatically
      toast({
        title: "Processing Stopped",
        description: "The job has been stopped. Extracted dates from processed chunks are still available.",
      })
    } catch (error) {
      console.error('Error stopping job:', error)
      toast({
        title: "Error",
        description: "Failed to stop the job. Please try again.",
        variant: "destructive",
      })
    }
  }

  // Query for job results
  const { data: jobData, refetch, isLoading, error } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => apiRequest(`/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (data) => {
      // Stop refetching if job is complete
      return data?.status === 'completed' || data?.status === 'failed' ? false : 2000
    }
  })

  // Set up Server-Sent Events for real-time progress
  useEffect(() => {
    if (!jobId || !jobData || ['completed', 'failed', 'cancelled'].includes(jobData.status)) {
      return
    }

    const baseUrl = getBaseUrl()
    const streamUrl = `${baseUrl}/jobs/${jobId}/stream`
    
    const es = new EventSource(streamUrl)
    setEventSource(es)

    es.onopen = () => {
      addLog('🔗 Connected to progress stream')
    }

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        handleProgressEvent(data)
      } catch (error) {
        console.error('Error parsing SSE data:', error)
        addLog(`❌ Error parsing progress data: ${error.message}`)
      }
    }

    es.onerror = (error) => {
      console.error('SSE connection error:', error)
      addLog('⚠️ Connection error')
    }

    return () => {
      es.close()
    }
  }, [jobId, jobData?.status])

  const addLog = (message) => {
    setLogs(prev => [...prev, {
      id: Date.now(),
      timestamp: new Date().toLocaleTimeString(),
      message
    }])
  }

  const handleProgressEvent = (data) => {
    switch (data.type) {
      case 'job_status':
      case 'progress':
        setProgress(data)
        break
      case 'job_started':
        addLog(`🎯 Job started processing ${data.totalChunks} chunks`)
        break
      case 'chunk_started':
        addLog(`🔄 Processing chunk ${data.chunkIndex}/${data.totalChunks}`)
        break
      case 'chunk_completed':
        addLog(`✅ Completed chunk ${data.chunkIndex}/${data.totalChunks} (${data.durationSec?.toFixed(1)}s)`)
        break
      case 'throttled':
        addLog(`⏳ Rate limited - waiting ${Math.round(data.waitMs/1000)}s`)
        break
      case 'relationship_analysis_started':
        addLog(`🔗 Analyzing relationships for ${data.totalDates} dates...`)
        break
      case 'relationship_analysis_completed':
        const summary = data.summary || {}
        addLog(`✨ Relationship analysis complete: ${summary.duplicatesRemoved || 0} duplicates removed, ${summary.datesResolved || 0} dates resolved (${data.durationSec?.toFixed(1)}s)`)
        break
      case 'relationship_analysis_failed':
        addLog(`⚠️ Relationship analysis failed: ${data.error} - using raw extracted dates`)
        break
      case 'job_completed':
        addLog(`🎉 Job completed! Found ${data.datesFound} dates`)
        refetch() // Refresh job data
        break
      case 'job_failed':
        addLog(`❌ Job failed: ${data.error}`)
        refetch()
        break
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast({
      title: "Copied!",
      description: "Date information copied to clipboard",
    })
  }

  const convertToCSV = (dates) => {
    if (!dates || dates.length === 0) {
      return 'No dates found'
    }
    
    // CSV headers
    const headers = [
      'Name',
      'Description', 
      'Value',
      'Document Name',
      'Chunk Number',
      'Section Header',
      'Total Chunks'
    ]
    
    // Convert each date object to CSV row
    const rows = dates.map(date => {
      return [
        `"${(date.name || '').replace(/"/g, '""')}"`,
        `"${(date.description || '').replace(/"/g, '""')}"`,
        `"${(date.value || '').replace(/"/g, '""')}"`,
        `"${(date.chunkLocation?.documentName || '').replace(/"/g, '""')}"`,
        date.chunkLocation?.chunkNumber || '',
        `"${(date.chunkLocation?.section || '').replace(/"/g, '""')}"`,
        date.chunkLocation?.totalChunks || ''
      ].join(',')
    })
    
    return [headers.join(','), ...rows].join('\n')
  }

  const downloadJSON = () => {
    const results = {
      jobId: jobData.jobId,
      status: jobData.status,
      extractedDates: jobData.dateList || [],
      metadata: {
        processedChunks: jobData.processedChunks,
        totalChunks: jobData.totalChunks,
        startTime: jobData.startTime,
        endTime: jobData.endTime,
        totalDuration: jobData.totalDurationSec
      }
    }
    
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `lease-dates-${jobId}.json`
    a.click()
    URL.revokeObjectURL(url)

    toast({
      title: "Download Started",
      description: "Results downloaded as JSON file",
    })
  }

  const downloadCSV = () => {
    const csvContent = convertToCSV(jobData.dateList || [])
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `lease-dates-${jobId}.csv`
    a.click()
    URL.revokeObjectURL(url)

    toast({
      title: "Download Started", 
      description: "Results downloaded as CSV file",
    })
  }

  if (!match) {
    return <div>Job not found</div>
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading job results...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <AlertCircle className="h-5 w-5 mr-2" />
              Error Loading Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 dark:text-gray-300 mb-4">{error.message}</p>
            <div className="flex gap-2">
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
              <Button onClick={() => setLocation('/')} variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const isProcessing = jobData.status === 'running' || jobData.status === 'initializing'
  const isCompleted = jobData.status === 'completed' || jobData.status === 'cancelled'
  const isFailed = jobData.status === 'failed'
  const isStopped = jobData.status === 'cancelled'
  const progressPercentage = progress?.percentage || (jobData.processedChunks / jobData.totalChunks * 100) || 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <AnalyticsHeader />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => setLocation('/')}
              className="mr-4"
              data-testid="button-back-home"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Home
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {isProcessing ? 'Processing Documents...' : 
                 isStopped ? 'Processing Stopped' :
                 isCompleted ? 'Extraction Complete' : 
                 isFailed ? 'Processing Failed' : 'Job Results'}
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                Job ID: {jobId}
              </p>
            </div>
          </div>
          
          {isCompleted && (
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setLocation(`/dashboard/${jobId}`)}
                data-testid="button-view-dashboard"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                View Dashboard
              </Button>
              <div className="relative" data-dropdown-container>
                <Button
                  onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600"
                  data-testid="button-download-results"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Results
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              
                {/* Custom dropdown */}
                {showDownloadDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10">
                    <div className="py-1">
                      <Tooltip content="For use with AI Agents">
                        <button
                          onClick={() => {
                            downloadJSON()
                            setShowDownloadDropdown(false)
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          data-testid="download-json"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Export as JSON
                        </button>
                      </Tooltip>
                      <Tooltip content="For use with Humans">
                        <button
                          onClick={() => {
                            downloadCSV()
                            setShowDownloadDropdown(false)
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          data-testid="download-csv"
                        >
                          <Table className="h-4 w-4 mr-2" />
                          Export as CSV
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Stopped job notification */}
        {isStopped && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-950 dark:border-yellow-800">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                <span className="text-yellow-800 dark:text-yellow-200 font-medium">
                  Processing was stopped early. Results from {jobData.processedChunks || 0} processed chunks are shown below.
                </span>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Results */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress Card (shown during processing) */}
            {isProcessing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Zap className="h-5 w-5 mr-2 text-blue-600" />
                      Processing Progress
                    </CardTitle>
                    <CardDescription>
                      AI is analyzing your documents and extracting lease dates (typically takes ~30 seconds per page)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between text-sm">
                        <span>Progress</span>
                        <span>{progressPercentage.toFixed(1)}%</span>
                      </div>
                      <Progress value={progressPercentage} className="h-3" />
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                        <span>{jobData.processedChunks || 0} / {jobData.totalChunks} chunks</span>
                        {progress?.eta && (
                          <span>ETA: {progress.eta.humanReadable}</span>
                        )}
                      </div>
                      
                      {/* Stop button */}
                      <div className="flex justify-center pt-2">
                        <Button
                          variant="outline" 
                          size="sm"
                          onClick={handleStopJob}
                          className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-950 dark:border-red-800 dark:text-red-400"
                          data-testid="button-stop-processing"
                        >
                          <Square className="h-4 w-4 mr-2" />
                          Stop Processing
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Results Card */}
            {(isCompleted || jobData.dateList?.length > 0) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-green-600" />
                        Extracted Dates ({jobData.dateList?.length || 0})
                      </div>
                      {isCompleted && (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                    </CardTitle>
                    <CardDescription>
                      Key lease dates found in your documents
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <AnimatePresence>
                        {(jobData.dateList || []).map((date, index) => (
                          <motion.div
                            key={`${date.name}-${date.value}-${index}`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow"
                            data-testid={`date-item-${index}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                  {date.name}
                                </h4>
                                <p className="text-blue-600 dark:text-blue-400 font-medium mb-2">
                                  {date.value}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                                  {date.description}
                                </p>
                                {date.chunkLocation && (
                                  <div className="inline-flex items-center px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs">
                                    <MapPin className="h-3 w-3 mr-1" />
                                    Chunk {date.chunkLocation.chunkNumber}/{date.chunkLocation.totalChunks} • {date.chunkLocation.documentName}
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(`${date.name}: ${date.value}`)}
                                data-testid={`button-copy-${index}`}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>

                      {jobData.dateList?.length === 0 && isCompleted && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No lease dates were found in the documents.</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Error Card */}
            {isFailed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Card className="border-red-200 dark:border-red-800">
                  <CardHeader>
                    <CardTitle className="flex items-center text-red-600">
                      <AlertCircle className="h-5 w-5 mr-2" />
                      Processing Failed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      {jobData.error || 'An unknown error occurred during processing.'}
                    </p>
                    <Button onClick={() => setLocation('/upload')} variant="outline">
                      Try Again
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Job Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Job Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Status</span>
                  <span className={`font-medium ${
                    isCompleted ? 'text-green-600' : 
                    isProcessing ? 'text-blue-600' : 
                    isFailed ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {jobData.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Documents</span>
                  <span className="font-medium">{jobData.documentNames?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Chunks</span>
                  <span className="font-medium">{jobData.totalChunks}</span>
                </div>
                <div className="flex justify-between">
                  <span>Processed</span>
                  <span className="font-medium">{jobData.processedChunks || 0}</span>
                </div>
                {jobData.totalDurationSec && (
                  <div className="flex justify-between">
                    <span>Duration</span>
                    <span className="font-medium">{formatDuration(jobData.totalDurationSec)}</span>
                  </div>
                )}
                {jobData.rateLimiterStats && (
                  <div className="flex justify-between">
                    <span>Tokens Used</span>
                    <span className="font-medium">{jobData.rateLimiterStats.totalTokens}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Processing Logs */}
            {(isProcessing || logs.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 mr-2" />
                      Processing Logs
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowLogs(!showLogs)}
                      data-testid="button-toggle-logs"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                {showLogs && (
                  <CardContent>
                    <div className="max-h-64 overflow-y-auto space-y-1 text-xs font-mono">
                      {logs.map((log) => (
                        <div key={log.id} className="flex text-gray-600 dark:text-gray-400">
                          <span className="text-gray-400 mr-2">{log.timestamp}</span>
                          <span>{log.message}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}